/**
 * Industry Benchmark Comparisons Page.
 *
 * Shows the organization's performance metrics alongside anonymized
 * industry averages. Each metric includes a rating indicator.
 */

import { TrendingUp, TrendingDown, Minus, BarChart3 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import Badge from "@/components/ui/Badge";
import Card from "@/components/ui/Card";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

interface BenchmarkMetric {
  name: string;
  unit: string;
  your_value: number | null;
  industry_avg: number | null;
  industry_best: number | null;
  rating: string;
}

interface BenchmarkComparison {
  period_months: number;
  participating_orgs: number;
  metrics: BenchmarkMetric[];
  your_summary: Record<string, unknown>;
  industry_summary: Record<string, unknown>;
}

const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    v,
  );

function BenchmarksPage() {
  const { data, isLoading } = useQuery<BenchmarkComparison>({
    queryKey: ["benchmarks"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/benchmarks/comparison?months=6"))
        .data as BenchmarkComparison,
  });

  if (isLoading || !data) return <LoadingSpinner className="py-12" />;

  const yours = data.your_summary as Record<string, unknown>;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[var(--text-primary)]">
          Industry Benchmarks
        </h2>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">
          Compare your performance against {data.participating_orgs} anonymized
          industry peers (last {data.period_months} months).
        </p>
      </div>

      {/* Your Summary */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Card padding="sm">
          <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
            Your Sales
          </p>
          <p className="text-xl font-bold text-[var(--text-primary)]">
            {yours.sales_count as number}
          </p>
        </Card>
        <Card padding="sm">
          <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
            Your Revenue
          </p>
          <p className="text-xl font-bold text-brand-600">
            {fmt(yours.total_revenue as number)}
          </p>
        </Card>
        <Card padding="sm">
          <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
            Your Profit
          </p>
          <p className="text-xl font-bold text-[var(--text-success)]">
            {fmt(yours.total_profit as number)}
          </p>
        </Card>
        <Card padding="sm">
          <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
            Inventory Value
          </p>
          <p className="text-xl font-bold text-[var(--text-primary)]">
            {fmt(yours.inventory_value as number)}
          </p>
        </Card>
      </div>

      {/* Benchmark Comparison Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 stagger-children">
        {data.metrics.map((metric) => {
          const ratingColor =
            metric.rating === "above_avg"
              ? "text-[var(--text-success)]"
              : metric.rating === "below_avg"
                ? "text-[var(--text-danger)]"
                : "text-[var(--text-secondary)]";
          const RatingIcon =
            metric.rating === "above_avg"
              ? TrendingUp
              : metric.rating === "below_avg"
                ? TrendingDown
                : Minus;
          const badgeVariant =
            metric.rating === "above_avg"
              ? "success"
              : metric.rating === "below_avg"
                ? "danger"
                : "default";

          return (
            <Card key={metric.name} className="hover-lift">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-sm font-semibold text-[var(--text-primary)]">
                    {metric.name}
                  </h3>
                  <Badge variant={badgeVariant}>
                    <RatingIcon className="h-3 w-3 mr-1 inline" />
                    {metric.rating === "above_avg"
                      ? "Above Average"
                      : metric.rating === "below_avg"
                        ? "Below Average"
                        : metric.rating === "insufficient_data"
                          ? "Insufficient Data"
                          : "Average"}
                  </Badge>
                </div>
                <BarChart3 className={`h-5 w-5 ${ratingColor}`} />
              </div>

              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-[10px] uppercase text-[var(--text-tertiary)]">
                    You
                  </p>
                  <p className={`text-lg font-bold ${ratingColor}`}>
                    {metric.your_value !== null ? `${metric.your_value}` : "—"}
                    <span className="text-xs font-normal ml-0.5">
                      {metric.unit}
                    </span>
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-[var(--text-tertiary)]">
                    Industry Avg
                  </p>
                  <p className="text-lg font-bold text-[var(--text-secondary)]">
                    {metric.industry_avg !== null
                      ? `${metric.industry_avg}`
                      : "—"}
                    <span className="text-xs font-normal ml-0.5">
                      {metric.unit}
                    </span>
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-[var(--text-tertiary)]">
                    Best
                  </p>
                  <p className="text-lg font-bold text-[var(--text-tertiary)]">
                    {metric.industry_best !== null
                      ? `${metric.industry_best}`
                      : "—"}
                    <span className="text-xs font-normal ml-0.5">
                      {metric.unit}
                    </span>
                  </p>
                </div>
              </div>

              {/* Visual bar */}
              {metric.your_value !== null &&
                metric.industry_avg !== null &&
                metric.industry_avg > 0 && (
                  <div className="mt-3 pt-3 border-t border-[var(--color-border-light)]">
                    <div className="h-2 rounded-full bg-[var(--color-surface-tertiary)] overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${metric.rating === "above_avg" ? "bg-emerald-500" : metric.rating === "below_avg" ? "bg-red-400" : "bg-brand-500"}`}
                        style={{
                          width: `${Math.min(100, (metric.your_value / (metric.industry_best || metric.industry_avg * 1.5)) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                )}
            </Card>
          );
        })}
      </div>

      <p className="text-center text-[10px] text-[var(--text-tertiary)]">
        Benchmarks are computed from anonymized, aggregated data across all
        TradeRoute organizations. No individual organization data is ever
        exposed.
      </p>
    </div>
  );
}

export default BenchmarksPage;
